/**
 * Leitura do access token no cliente (RN-605).
 *
 * O que o front faz com isto: saber **quem** está logado, **quais empresas**
 * estão vinculadas e **quando** o token expira, para renovar antes de gastar
 * uma requisição em um 401 previsível.
 *
 * O que o front **não** faz: decidir permissão. A assinatura não é verificada
 * aqui — não há como verificá-la sem o segredo, e ele não pode viver no
 * navegador. Qualquer decisão de autorização é do servidor; `isAdmin` e os
 * papéis lidos daqui servem só para escolher o que **mostrar**. Esconder um
 * botão não protege um endpoint.
 */

/**
 * Espelho de `PapelUsuario` do `@deca/domain`.
 *
 * O web não importa o domínio por decisão de arquitetura (`docs/arquitetura.md`,
 * "quem pode importar quem"): consome apenas tipos do contrato HTTP. Se a lista
 * mudar no domínio, muda aqui junto — está citada no INTEGRACAO.md.
 */
export type PapelUsuario = 'ADMIN' | 'STAFF' | 'BUSINESS_OWNER' | 'BUSINESS_EMPLOYER';

export interface VinculoDoToken {
  readonly businessId: string;
  readonly papeis: readonly PapelUsuario[];
}

export interface IdentidadeDoToken {
  readonly accountId: string;
  readonly personId: string;
  readonly isAdmin: boolean;
  readonly vinculos: readonly VinculoDoToken[];
  /** Momento da expiração em epoch ms; `null` quando o token não declara `exp`. */
  readonly expiraEm: number | null;
}

/** Payload emitido por `apps/api/src/modules/auth/jwt-payload.ts`. */
interface PayloadAcesso {
  sub?: unknown;
  pid?: unknown;
  adm?: unknown;
  biz?: unknown;
  typ?: unknown;
  exp?: unknown;
}

/** Devolve a identidade do token, ou `null` se ele estiver malformado. */
export function lerAccessToken(token: string): IdentidadeDoToken | null {
  const partes = token.split('.');
  if (partes.length !== 3) return null;

  const corpo = partes[1];
  if (corpo === undefined) return null;

  let payload: PayloadAcesso;
  try {
    const json: unknown = JSON.parse(decodificarBase64Url(corpo));
    if (typeof json !== 'object' || json === null) return null;
    payload = json;
  } catch {
    return null;
  }

  if (typeof payload.sub !== 'string' || typeof payload.pid !== 'string') return null;

  return {
    accountId: payload.sub,
    personId: payload.pid,
    isAdmin: payload.adm === true,
    vinculos: lerVinculos(payload.biz),
    expiraEm: typeof payload.exp === 'number' ? payload.exp * 1000 : null,
  };
}

function lerVinculos(biz: unknown): VinculoDoToken[] {
  if (!Array.isArray(biz)) return [];

  return biz.flatMap((item): VinculoDoToken[] => {
    if (typeof item !== 'object' || item === null) return [];
    const { i, r } = item as { i?: unknown; r?: unknown };
    if (typeof i !== 'string') return [];
    const papeis = Array.isArray(r) ? r.filter(ehPapel) : [];
    return [{ businessId: i, papeis }];
  });
}

function ehPapel(valor: unknown): valor is PapelUsuario {
  return (
    valor === 'ADMIN' ||
    valor === 'STAFF' ||
    valor === 'BUSINESS_OWNER' ||
    valor === 'BUSINESS_EMPLOYER'
  );
}

/**
 * Token expirado, ou a menos de `margemSegundos` de expirar.
 *
 * A margem existe para não enviar uma requisição que já sai condenada: entre
 * montar o pedido e ele chegar ao servidor, um token com 2 segundos de vida
 * vira um 401 garantido.
 */
export function tokenExpirado(identidade: IdentidadeDoToken, margemSegundos = 30): boolean {
  if (identidade.expiraEm === null) return false;
  return identidade.expiraEm - margemSegundos * 1000 <= Date.now();
}

function decodificarBase64Url(valor: string): string {
  const base64 = valor.replace(/-/g, '+').replace(/_/g, '/');
  const preenchido = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
  const binario = atob(preenchido);
  // O payload pode conter acentos; `atob` devolve bytes, não texto.
  const bytes = Uint8Array.from(binario, (caractere) => caractere.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}
